/**
 * Pure path utilities for the routing shim.
 *
 * These are router-agnostic string algorithms reimplemented from React
 * Router's `resolvePath` / `matchPath` so the shim can preserve byte-identical
 * relative-navigation and pattern-matching behavior without depending on
 * `react-router-dom`. TanStack Router has its own (differently-shaped)
 * resolver; the public shim surface keeps the React Router semantics the
 * codebase was written against.
 */

export type Path = {
  pathname: string;
  search: string;
  hash: string;
};

export type To = string | Partial<Path>;

/** Join path segments into a single pathname, collapsing duplicate slashes. */
export function joinPaths(paths: Array<string | undefined>): string {
  const joined = paths.filter((segment): segment is string => !!segment).join('/');
  const cleaned = joined.replace(/\/{2,}/g, '/');
  return cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
}

/** Split a path string into `{ pathname, search, hash }` (React Router shape). */
export function parsePath(path: string): Partial<Path> {
  const parsedPath: Partial<Path> = {};

  if (!path) {
    return parsedPath;
  }

  let rest = path;

  const hashIndex = rest.indexOf('#');
  if (hashIndex >= 0) {
    parsedPath.hash = rest.substring(hashIndex);
    rest = rest.substring(0, hashIndex);
  }

  const searchIndex = rest.indexOf('?');
  if (searchIndex >= 0) {
    parsedPath.search = rest.substring(searchIndex);
    rest = rest.substring(0, searchIndex);
  }

  if (rest) {
    parsedPath.pathname = rest;
  }

  return parsedPath;
}

/** Recombine a `{ pathname, search, hash }` shape into a path string. */
export function createPath({ pathname = '/', search = '', hash = '' }: Partial<Path>): string {
  let path = pathname;
  if (search && search !== '?') {
    path += search.startsWith('?') ? search : `?${search}`;
  }
  if (hash && hash !== '#') {
    path += hash.startsWith('#') ? hash : `#${hash}`;
  }
  return path;
}

/**
 * Resolve a relative pathname against a base pathname, honoring `.` and `..`
 * segments. Mirrors React Router's `resolvePathname`.
 */
function resolvePathname(relativePath: string, fromPathname: string): string {
  const segments = fromPathname.replace(/\/+$/, '').split('/');
  const relativeSegments = relativePath.split('/');

  relativeSegments.forEach(segment => {
    if (segment === '..') {
      // Keep the root slash ('') if we hit the top.
      if (segments.length > 1) {
        segments.pop();
      }
    } else if (segment !== '.') {
      segments.push(segment);
    }
  });

  return segments.length > 1 ? segments.join('/') : '/';
}

function resolveToPathname(toPathname: string | undefined, fromPathname: string): string {
  if (!toPathname) {
    return fromPathname;
  }
  return toPathname.startsWith('/') ? toPathname : resolvePathname(toPathname, fromPathname);
}

/**
 * Resolve a `To` against a base pathname into a full `{ pathname, search, hash }`.
 * Mirrors React Router's `resolvePath`. Absolute `to` values pass through; bare
 * and `.`/`..` relative values resolve against `fromPathname`.
 */
export function resolvePath(to: To, fromPathname = '/'): Path {
  const { pathname: toPathname, search = '', hash = '' } = typeof to === 'string' ? parsePath(to) : to;

  return { pathname: resolveToPathname(toPathname, fromPathname), search, hash };
}

export type PathMatch<ParamKey extends string = string> = {
  params: Record<ParamKey, string | undefined>;
  pathname: string;
  pattern: { path: string; caseSensitive: boolean; end: boolean };
};

const compileCache = new Map<string, { matcher: RegExp; paramNames: string[] }>();

/**
 * Compile a React-Router-style path pattern (`:param`, trailing `*`) into a
 * regular expression plus the ordered param names. Cached by pattern shape.
 */
function compilePath(path: string, caseSensitive: boolean, end: boolean): { matcher: RegExp; paramNames: string[] } {
  const cacheKey = `${path}|${caseSensitive}|${end}`;
  const cached = compileCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const paramNames: string[] = [];
  let regexpSource =
    '^' +
    path
      .replace(/\/*\*?$/, '') // ignore trailing slashes and the wildcard, handled below
      .replace(/^\/*/, '/') // ensure a leading slash
      .replace(/[\\.*+^${}|()[\]]/g, '\\$&') // escape regex special chars
      .replace(/\/:([\w-]+)(\?)?/g, (_match, paramName: string, optional?: string) => {
        paramNames.push(paramName);
        return optional ? '/?([^\\/]+)?' : '/([^\\/]+)';
      });

  if (path.endsWith('*')) {
    paramNames.push('*');
    regexpSource += path === '*' || path === '/*' ? '(.*)$' : '(?:\\/(.+)|\\/*)$';
  } else if (end) {
    // Allow an optional trailing slash on exact matches.
    regexpSource += '\\/*$';
  } else {
    // Non-terminal: match a path prefix at a segment boundary (or end).
    regexpSource += '(?:\\b|\\/|$)';
  }

  const matcher = new RegExp(regexpSource, caseSensitive ? undefined : 'i');
  const result = { matcher, paramNames };
  compileCache.set(cacheKey, result);
  return result;
}

function safelyDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export type PathPattern = string | { path: string; caseSensitive?: boolean; end?: boolean };

/**
 * Match a pathname against a React-Router-style pattern. Returns the match
 * (with decoded params) or `null`. Mirrors React Router's `matchPath`.
 */
export function matchPath<ParamKey extends string = string>(
  pattern: PathPattern,
  pathname: string,
): PathMatch<ParamKey> | null {
  const { path, caseSensitive = false, end = true } = typeof pattern === 'string' ? { path: pattern } : pattern;

  const { matcher, paramNames } = compilePath(path, caseSensitive, end);
  const match = pathname.match(matcher);
  if (!match) {
    return null;
  }

  const matchedPathname = match[0];
  const captureGroups = match.slice(1);

  const params = paramNames.reduce<Record<string, string | undefined>>((memo, paramName, index) => {
    const value = captureGroups[index];
    memo[paramName] = value != null ? safelyDecodeURIComponent(value) : value;
    return memo;
  }, {});

  return {
    params: params as Record<ParamKey, string | undefined>,
    pathname: matchedPathname,
    pattern: { path, caseSensitive, end },
  };
}
